import React from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownSelect } from './DropdownSelect';
import { InputGroup } from './InputGroup';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterProfileBarProps {
  filters: {
    role: string;
    status: string;
    city: string;
    obnl_review_status: string;
    search: string;
  };
  onFilterChange: (name: keyof FilterProfileBarProps['filters'], value: string) => void;
}

const FilterProfileBar: React.FC<FilterProfileBarProps> = ({ filters, onFilterChange }) => {
  const { t } = useTranslation(['admin', 'common']);

  const roleOptions: FilterOption[] = [
    { value: '', label: t('all_roles', { ns: 'common', defaultValue: 'Tous les rôles' }) },
    { value: 'freelancer', label: t('role.freelancer', { ns: 'common' }) },
    { value: 'recruiter', label: t('role.recruiter', { ns: 'common' }) },
    { value: 'administrator', label: t('role.administrator', { ns: 'common' }) },
  ];

  const statusOptions: FilterOption[] = [
    { value: '', label: t('all_statuses', { ns: 'common', defaultValue: 'Tous les statuts' }) },
    { value: 'active', label: t('user_status.active', { ns: 'common' }) },
    { value: 'inactive', label: t('user_status.inactive', { ns: 'common' }) },
    { value: 'banned', label: t('user_status.banned', { ns: 'common' }) },
  ];

  const obnlReviewStatusOptions: FilterOption[] = [
    {
      value: '',
      label: t('all_obnl_review_statuses', {
        ns: 'common',
        defaultValue: 'All OBNL review statuses',
      }),
    },
    {
      value: 'not_applicable',
      label: t('obnl_review_status.not_applicable', { ns: 'common' }),
    },
    { value: 'pending', label: t('obnl_review_status.pending', { ns: 'common' }) },
    { value: 'approved', label: t('obnl_review_status.approved', { ns: 'common' }) },
    { value: 'rejected', label: t('obnl_review_status.rejected', { ns: 'common' }) },
  ];

  return (
    <div className="flex flex-wrap gap-3 items-end justify-end px-4 py-3 border-b border-gray-300 bg-gray-50">
      <div className="min-w-64 flex-1">
        <InputGroup
          type="search"
          value={filters.search}
          onChange={e => onFilterChange('search', e.target.value)}
          aria-label={t('user_search_label', { ns: 'admin', defaultValue: 'Search users' })}
          placeholder={t('user_search_placeholder', {
            ns: 'admin',
            defaultValue: 'Name, email, phone or company...',
          })}
        />
      </div>

      <DropdownSelect<FilterOption>
        className="w-48"
        value={roleOptions.find(option => option.value === filters.role) ?? null}
        onChange={(_label, item) => onFilterChange('role', item?.value ?? '')}
        options={roleOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value || 'all'}
        placeholder={t('all_roles', { ns: 'common', defaultValue: 'Tous les rôles' })}
      />

      <DropdownSelect<FilterOption>
        className="w-48"
        value={statusOptions.find(option => option.value === filters.status) ?? null}
        onChange={(_label, item) => onFilterChange('status', item?.value ?? '')}
        options={statusOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value || 'all'}
        placeholder={t('all_statuses', { ns: 'common', defaultValue: 'Tous les statuts' })}
      />

      <div className="w-48">
        <InputGroup
          value={filters.city}
          onChange={e => onFilterChange('city', e.target.value)}
          placeholder={t('table_fields.city', { ns: 'admin', defaultValue: 'Ville' }) + '...'}
        />
      </div>

      <DropdownSelect<FilterOption>
        className="w-56"
        value={
          obnlReviewStatusOptions.find(option => option.value === filters.obnl_review_status) ??
          null
        }
        onChange={(_label, item) => onFilterChange('obnl_review_status', item?.value ?? '')}
        options={obnlReviewStatusOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value || 'all'}
        placeholder={t('all_obnl_review_statuses', {
          ns: 'common',
          defaultValue: 'All OBNL review statuses',
        })}
      />
    </div>
  );
};

export default FilterProfileBar;
